import { createContext, useContext, useEffect, useMemo } from "react";
import { IMask } from "react-imask";
import { type InputProps } from "ui";

export interface MaskedInputRegistryContextValue {
  blur: (id: string, element: HTMLInputElement) => void;
  cleanup: (id: string) => void;
  focus: (id: string, element: HTMLInputElement, key: string) => void;
  getKey: (id: string, value: string) => string;
}

export interface FocusedInput {
  element: HTMLInputElement;
  key: string;
}

export const maskedInputRegistryContext =
  createContext<MaskedInputRegistryContextValue | null>(null);

export interface MaskedInputRegistryHandlers {
  cleanup: () => void;
  key: string;
  onBlur: (element: HTMLInputElement) => void;
  onFocus: (element: HTMLInputElement) => void;
}

export function useMaskedInputRegistryHandlers(
  id: string,
  value: string,
): MaskedInputRegistryHandlers {
  const registry = useContext(maskedInputRegistryContext);
  const key = registry?.getKey(id, value) ?? value;

  return useMemo(() => {
    if (!registry) {
      return {
        cleanup: () => {},
        key,
        onBlur: () => {},
        onFocus: () => {},
      };
    }

    return {
      cleanup: () => {
        registry.cleanup(id);
      },
      key,
      onBlur: (element: HTMLInputElement) => {
        registry.blur(id, element);
      },
      onFocus: (element: HTMLInputElement) => {
        registry.focus(id, element, key);
      },
    };
  }, [id, key, registry]);
}

export function useMaskedInputRegistryCleanup(
  registryHandlers: MaskedInputRegistryHandlers,
) {
  useEffect(() => {
    return () => {
      registryHandlers.cleanup();
    };
  }, [registryHandlers]);
}

export interface MaskedAttributeInputProps {
  defaultValue: string;
  disabled: boolean;
  onAccept: (value: string) => void;
  registryHandlers: MaskedInputRegistryHandlers;
}

export interface TempoInputProps extends Omit<
  InputProps,
  "value" | "onChange"
> {
  inputId?: string;
  value: string;
  onChange: (e: string) => void;
  disabled?: boolean;
}

const tempoEnum = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "X"];

export const tempoMaskConfig = {
  mask: IMask.MaskedEnum,
  enum: tempoEnum,
  placeholderChar: "0",
  maxLength: 1,
};

export interface MaskedTempoInputProps extends TempoInputProps {
  registryHandlers: MaskedInputRegistryHandlers;
}
